"use client";

import React, { useState, useEffect } from "react";
import { supabase } from "../supabase/client";
import { Save, Plus, Trash2, Languages, AlertCircle } from "lucide-react";

export default function PrivacyAdmin() {
  const [activeLang, setActiveLang] = useState("en");
  const [id, setId] = useState(null);
  const [contentEn, setContentEn] = useState([]);
  const [contentBn, setContentBn] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPolicy();
  }, []);

  async function fetchPolicy() {
    const { data } = await supabase.from("privacy_policies").select("*").eq("is_active", true).single();
    if (data) {
      setId(data.id);
      setContentEn(data.content_en);
      setContentBn(data.content_bn);
    }
    setLoading(false);
  }

  const handleUpdateSection = (index, field, value) => {
    if (activeLang === "en") {
      const updated = [...contentEn];
      updated[index][field] = value;
      setContentEn(updated);
    } else {
      const updated = [...contentBn];
      updated[index][field] = value;
      setContentBn(updated);
    }
  };

  const addSection = () => {
    const newSection = { heading: "", body: "" };
    activeLang === "en" ? setContentEn([...contentEn, newSection]) : setContentBn([...contentBn, newSection]);
  };

  const removeSection = (index) => {
    activeLang === "en" 
      ? setContentEn(contentEn.filter((_, i) => i !== index)) 
      : setContentBn(contentBn.filter((_, i) => i !== index));
  };

  async function savePolicy() {
    setSaving(true);
    const { error } = await supabase
      .from("privacy_policies")
      .update({ content_en: contentEn, content_bn: contentBn })
      .eq("id", id);

    if (!error) alert("Policy updated successfully!");
    setSaving(false);
  }

  if (loading) return <div className="p-10 text-center font-black animate-pulse">LOADING SYSTEM...</div>;

  const currentContent = activeLang === "en" ? contentEn : contentBn;

  return (
    <div className="p-6 max-w-6xl mx-auto bg-white min-h-screen">
      {/* Admin Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-10">
        <div>
          <h1 className="text-3xl font-black italic uppercase tracking-tighter">Manage Privacy</h1>
          <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mt-1">Configuring Legal Metadata</p>
        </div>

        <div className="flex gap-3">
          <button 
            onClick={() => setActiveLang(activeLang === "en" ? "bn" : "en")}
            className="flex items-center gap-2 px-4 py-2 border-2 border-black font-black text-[10px] uppercase tracking-widest hover:bg-black hover:text-white transition-all"
          >
            <Languages size={14} /> {activeLang === "en" ? "Switch to Bangla" : "Switch to English"}
          </button>
          <button 
            onClick={savePolicy}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2 bg-pink-600 text-white font-black text-[10px] uppercase tracking-widest hover:bg-black transition-all disabled:opacity-50"
          >
            <Save size={14} /> {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      {/* Editor UI */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-6">
          {currentContent.map((section, index) => (
            <div key={index} className="group relative p-6 border-2 border-gray-100 rounded-2xl hover:border-pink-200 transition-colors">
              <button 
                onClick={() => removeSection(index)}
                className="absolute top-4 right-4 text-gray-300 hover:text-red-500 transition-colors"
              >
                <Trash2 size={18} />
              </button>
              
              <div className="mb-4">
                <label className="text-[10px] font-black uppercase text-gray-400 mb-2 block">Heading ({activeLang})</label>
                <input 
                  type="text"
                  value={section.heading}
                  onChange={(e) => handleUpdateSection(index, "heading", e.target.value)}
                  className="w-full text-lg font-bold bg-transparent border-b-2 border-gray-50 focus:border-pink-600 outline-none pb-2"
                  placeholder="e.g. Data Collection"
                />
              </div>

              <div>
                <label className="text-[10px] font-black uppercase text-gray-400 mb-2 block">Content Body</label>
                <textarea 
                  rows={4}
                  value={section.body}
                  onChange={(e) => handleUpdateSection(index, "body", e.target.value)}
                  className="w-full bg-gray-50 p-4 rounded-xl text-sm font-medium leading-relaxed outline-none focus:ring-2 focus:ring-pink-100 transition-all"
                  placeholder="Explain how you handle data..."
                />
              </div>
            </div>
          ))}

          <button 
            onClick={addSection}
            className="w-full py-4 border-2 border-dashed border-gray-200 rounded-2xl text-gray-400 font-black text-[10px] uppercase tracking-[0.2em] hover:border-pink-600 hover:text-pink-600 transition-all flex items-center justify-center gap-2"
          >
            <Plus size={16} /> Add New Section
          </button>
        </div>

        {/* Sidebar Info */}
        <div className="lg:col-span-4 space-y-6">
          <div className="bg-black text-white p-6 rounded-[2rem]">
            <div className="flex items-center gap-2 mb-4 text-pink-500">
              <AlertCircle size={20} />
              <h3 className="text-xs font-black uppercase tracking-widest">Admin Tip</h3>
            </div>
            <p className="text-sm font-medium text-gray-300 leading-relaxed">
              When editing for **Curve & Fit**, ensure the Bangla translation matches the English intent exactly. Use the "Blind Packaging" section to emphasize customer safety.
            </p>
          </div>

          <div className="p-6 border-2 border-gray-100 rounded-[2rem]">
             <h3 className="text-xs font-black uppercase tracking-widest mb-4">Quick Stats</h3>
             <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400 font-bold uppercase">English Sections</span>
                  <span className="text-sm font-black italic">{contentEn.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400 font-bold uppercase">Bangla Sections</span>
                  <span className="text-sm font-black italic">{contentBn.length}</span>
                </div>
             </div>
          </div>
        </div>
      </div>
    </div>
  );
}